/** Default 40 MiB; must match Laravel `MAX_CLAIM_ATTACHMENT_FILE_BYTES` / `REACT_APP_MAX_ATTACHMENT_FILE_KB * 1024`. */
const DEFAULT_MAX_CLAIM_ATTACHMENT_FILE_BYTES = 40960 * 1024;

// Fetch config.json at runtime
let configCache: {
    readonly APP_MODE: 'local' | 'test' | 'production';
    readonly API_URL: string;
    readonly PORT: number;
    readonly MAX_CLAIM_ATTACHMENT_FILE_BYTES: number;
} | null = null;

function resolveMaxAttachmentBytesFromJson(raw: unknown): number {
    if (raw === undefined || raw === null || raw === '') {
        return DEFAULT_MAX_CLAIM_ATTACHMENT_FILE_BYTES;
    }
    const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10);
    if (!Number.isFinite(n) || n < 1) {
        return DEFAULT_MAX_CLAIM_ATTACHMENT_FILE_BYTES;
    }
    return Math.floor(n);
}

export const loadConfig = async (): Promise<void> => {
    if (configCache) {
        return;
    }

    try {
        const response = await fetch('/config.json');
        if (!response.ok) {
            throw new Error(`Failed to load config: ${response.statusText}`);
        }
        const siteConfig = await response.json() as {
            APP_MODE?: string;
            API_URL?: string;
            PORT?: number;
            MAX_CLAIM_ATTACHMENT_FILE_BYTES?: number;
        };

        const appMode = (siteConfig.APP_MODE || 'local') as 'local' | 'test' | 'production';

        configCache = {
            APP_MODE: appMode,
            API_URL: siteConfig.API_URL || 'http://localhost/api',
            PORT: siteConfig.PORT || 3001,
            MAX_CLAIM_ATTACHMENT_FILE_BYTES: resolveMaxAttachmentBytesFromJson(
                siteConfig.MAX_CLAIM_ATTACHMENT_FILE_BYTES
            ),
        } as const;
    } catch (error) {
        console.error('Error loading config.json, using defaults:', error);
        // Fallback to defaults if config.json can't be loaded
        configCache = {
            APP_MODE: 'local' as const,
            API_URL: 'http://localhost/api',
            PORT: 3001,
            MAX_CLAIM_ATTACHMENT_FILE_BYTES: DEFAULT_MAX_CLAIM_ATTACHMENT_FILE_BYTES,
        } as const;
    }
};

// Export config object (will be populated after loadConfig() is called)
export const config: {
    readonly APP_MODE: 'local' | 'test' | 'production';
    readonly API_URL: string;
    readonly PORT: number;
    readonly MAX_CLAIM_ATTACHMENT_FILE_BYTES: number;
} = {
    get APP_MODE() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.APP_MODE;
    },
    get API_URL() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.API_URL;
    },
    get PORT() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.PORT;
    },
    get MAX_CLAIM_ATTACHMENT_FILE_BYTES() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.MAX_CLAIM_ATTACHMENT_FILE_BYTES;
    },
} as any; 